import React, { useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Flex, Grid, IconMapPin, Text, csx } from '@vtex/admin-ui'

import { DrawerFooter, DrawerBody } from '../../../../../../ui/components'
import { MethodSelectItem } from '../MethodSelectItem'
import { useOrderFormShippingEstimate } from '../../../../hooks'
import { MethodLabelPrice } from './MethodLabelPrice'
import type { Package } from '../../../../../../core/typings'
import { DeliveryChannel } from '../../../../constants'
import { StepSubmitButton } from '../../../../components'
import { Badge } from '../ShippingPackage/Badge'
import { decorateDeliverySlaWithBestIndicators } from '../../utils/sla'

const getInitialSelectedDeliveryMethodSla = (currentPackage: Package) => {
  return currentPackage.selectedSlaType === DeliveryChannel.Delivery
    ? currentPackage.selectedSlaObj
    : null
}

export type DeliveryMethodsContentProps = {
  currentPackage: Package
  onDeliveryMethodSubmit: (selectedDeliveryMethodSla: SLA) => void
  deliveryMethodsList: SLA[]
  isOpen: boolean
}

export const DeliveryMethodsContent = ({
  currentPackage,
  deliveryMethodsList,
  onDeliveryMethodSubmit,
  isOpen,
}: DeliveryMethodsContentProps) => {
  const { getEstimateMessage } = useOrderFormShippingEstimate()

  const [
    selectedDeliveryMethodSla,
    setSelectedDeliveryMethodSla,
  ] = useState<SLA>()

  const checkItemIsSelected = (deliveryItem: SLA) => {
    return deliveryItem.id === selectedDeliveryMethodSla?.id
  }

  const handleMethodItemClick = (deliveryMethodSla: SLA) => () => {
    setSelectedDeliveryMethodSla(deliveryMethodSla)
  }

  const handleDeliveryMethodSelectionSubmit = () => {
    if (selectedDeliveryMethodSla) {
      onDeliveryMethodSubmit(selectedDeliveryMethodSla)
    }
  }

  const deliveryMethodsListDecoratedWithIndicators = useMemo(() => {
    return deliveryMethodsList.map((sla) =>
      decorateDeliverySlaWithBestIndicators(currentPackage, sla)
    )
  }, [currentPackage, deliveryMethodsList])

  useEffect(() => {
    if (isOpen) {
      const deliveryMethodSla = getInitialSelectedDeliveryMethodSla(
        currentPackage
      )

      if (deliveryMethodSla) {
        setSelectedDeliveryMethodSla(deliveryMethodSla)
      }
    }
  }, [isOpen, currentPackage])

  return (
    <>
      <DrawerBody>
        <Grid rowGap="0.5rem">
          {deliveryMethodsListDecoratedWithIndicators.map(
            (deliveryMethodItem) => (
              <MethodSelectItem
                key={deliveryMethodItem.id}
                data-testid="shipping-method-drawer-delivery-item"
                icon={<IconMapPin />}
                title={deliveryMethodItem.name ?? ''}
                selected={checkItemIsSelected(deliveryMethodItem)}
                onClick={handleMethodItemClick(deliveryMethodItem)}
                middleColumn={
                  <Flex
                    justify="space-between"
                    align="center"
                    className={csx({ marginTop: '$space-4' })}
                  >
                    <Text>
                      {getEstimateMessage(
                        deliveryMethodItem.shippingEstimate ?? ''
                      )}
                    </Text>
                    {deliveryMethodItem.isCheapest && (
                      <Badge>
                        <FormattedMessage id="checkout.shipping.sla-cheapest" />
                      </Badge>
                    )}
                    {deliveryMethodItem.isFatest && (
                      <Badge>
                        <FormattedMessage id="checkout.shipping.sla-fastest" />
                      </Badge>
                    )}
                  </Flex>
                }
                rightColumn={
                  <MethodLabelPrice
                    selected={checkItemIsSelected(deliveryMethodItem)}
                    price={deliveryMethodItem.price}
                  />
                }
              />
            )
          )}
        </Grid>
      </DrawerBody>
      <DrawerFooter className={csx({ marginBottom: '$space-2' })}>
        <StepSubmitButton
          disabled={!selectedDeliveryMethodSla}
          onClick={handleDeliveryMethodSelectionSubmit}
          data-testid="continue-button"
        >
          <FormattedMessage id="continue" />
        </StepSubmitButton>
      </DrawerFooter>
    </>
  )
}
